import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import {
  ClipboardDocumentListIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useWorksheets } from '../hooks/useWorksheets';
import { useCustomers } from '../hooks/useCustomers';
import { useLanguage } from '../contexts/LanguageContext';
import { Worksheet, WORKSHEET_STATUSES, calculateWorksheetTotals } from '../types/worksheet';
import DigitalSignature from '../components/common/DigitalSignature';
import { generateWorksheetPdf } from '../lib/worksheetPdfGenerator';

const PublicWorksheetView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { getWorksheetByPublicToken, addSignature } = useWorksheets();
  const { customers } = useCustomers();
  const { t } = useLanguage();
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [showSignatureModal, setShowSignatureModal] = useState(false);

  useEffect(() => {
    if (!token) return;
    getWorksheetByPublicToken(token).then(ws => {
      if (ws) {
        setWorksheet(ws);
      } else {
        setError(true);
      }
      setLoading(false);
    });
  }, [token, getWorksheetByPublicToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  if (error || !worksheet) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ClipboardDocumentListIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">Munkalap nem található</h2>
          <p className="text-gray-500">A link lehet, hogy lejárt vagy érvénytelen.</p>
        </div>
      </div>
    );
  }

  const customer = customers.find(c => c.id === worksheet.customerId);
  const customerName = customer?.companyName || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || 'Ismeretlen ügyfél';
  const totals = calculateWorksheetTotals(worksheet.items || []);
  const statusInfo = WORKSHEET_STATUSES.find(s => s.value === worksheet.status);

  const handleDownloadPdf = () => {
    generateWorksheetPdf({ worksheet, timeEntries: [], customerName, t });
  };

  const handleSignature = async (signatureData: string, signerName: string, signerRole: string) => {
    if (!worksheet) return;
    await addSignature(worksheet.id, {
      signedBy: signerName,
      signerName,
      signerRole,
      signatureData,
      signedAt: new Date(),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded bg-indigo-50 border border-indigo-200 mr-3">
                <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{worksheet.title}</h1>
                <p className="text-sm text-gray-500">{worksheet.worksheetNumber}</p>
              </div>
            </div>
            {statusInfo && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                {statusInfo.labelKey.split('.').pop()}
              </span>
            )}
          </div>
        </div>

        {/* Customer info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Ügyfél</h2>
          <div className="flex items-center">
            <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-gray-900">{customerName}</span>
          </div>
          {worksheet.location && (
            <div className="flex items-center mt-2">
              <MapPinIcon className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-gray-700">{worksheet.location}</span>
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Dátumok</h2>
          <div className="grid grid-cols-2 gap-4">
            {worksheet.worksheetDate && (
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-xs text-gray-500">Munka dátuma</div>
                  <div className="text-sm text-gray-900">
                    {format(new Date(worksheet.worksheetDate), 'yyyy. MM. dd.', { locale: hu })}
                  </div>
                </div>
              </div>
            )}
            {worksheet.startTime && worksheet.endTime && (
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-xs text-gray-500">Időtartam</div>
                  <div className="text-sm text-gray-900">{worksheet.startTime} - {worksheet.endTime}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        {worksheet.items && worksheet.items.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
            <h2 className="text-sm font-medium text-gray-500 mb-3">Tételek</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-600">Megnevezés</th>
                  <th className="text-right py-2 text-gray-600">Menny.</th>
                  <th className="text-right py-2 text-gray-600">Egységár</th>
                  <th className="text-right py-2 text-gray-600">Nettó</th>
                </tr>
              </thead>
              <tbody>
                {worksheet.items.filter(i => i.type !== 'header').map(item => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 text-gray-900">{item.name}</td>
                    <td className="text-right py-2 text-gray-700">{item.quantity} {item.unit}</td>
                    <td className="text-right py-2 text-gray-700">{item.unitPrice.toLocaleString('hu-HU')} Ft</td>
                    <td className="text-right py-2 text-gray-900 font-medium">{item.totalNet.toLocaleString('hu-HU')} Ft</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={3} className="text-right py-3 font-medium text-gray-700">Összesen (nettó):</td>
                  <td className="text-right py-3 font-bold text-gray-900">{totals.totalNet.toLocaleString('hu-HU')} Ft</td>
                </tr>
                <tr>
                  <td colSpan={3} className="text-right py-1 font-medium text-gray-700">ÁFA:</td>
                  <td className="text-right py-1 text-gray-900">{totals.totalVat.toLocaleString('hu-HU')} Ft</td>
                </tr>
                <tr>
                  <td colSpan={3} className="text-right py-1 font-medium text-gray-700">Bruttó:</td>
                  <td className="text-right py-1 font-bold text-gray-900">{totals.totalGross.toLocaleString('hu-HU')} Ft</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Notes visible to customer */}
        {worksheet.notes_list && worksheet.notes_list.filter(n => n.visibility === 'customer').length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
            <h2 className="text-sm font-medium text-gray-500 mb-3">Megjegyzések</h2>
            <div className="space-y-2">
              {worksheet.notes_list.filter(n => n.visibility === 'customer').map(note => (
                <div key={note.id} className="text-sm text-gray-700 bg-gray-50 rounded p-3">
                  {note.text}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signatures */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Aláírások</h2>
          {worksheet.signatures && worksheet.signatures.length > 0 && (
            <div className="space-y-3 mb-4">
              {worksheet.signatures.map(sig => (
                <div key={sig.id} className="flex items-center gap-3 border-b border-gray-100 pb-2">
                  <img src={sig.signatureData} alt="Aláírás" className="h-16 border border-gray-200 rounded" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{sig.signerName}</div>
                    <div className="text-xs text-gray-500">{sig.signerRole}</div>
                    <div className="text-xs text-gray-400">
                      {format(new Date(sig.signedAt as Date), 'yyyy. MM. dd. HH:mm', { locale: hu })}
                    </div>
                  </div>
                  <CheckCircleIcon className="h-5 w-5 text-green-500 ml-auto" />
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => setShowSignatureModal(true)}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <PencilIcon className="h-4 w-4 inline mr-1" />
            Aláírás hozzáadása
          </button>
          <DigitalSignature isOpen={showSignatureModal} onClose={() => setShowSignatureModal(false)} onSave={handleSignature} />
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <button
            onClick={handleDownloadPdf}
            className="px-6 py-2.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5 inline mr-2" />
            PDF letöltése
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicWorksheetView;
